/**
 * What the agent did during a turn — one row per tool call, in the shape
 * `bimgo-ai-web`'s `ToolSection` gives them.
 *
 * The transcript would otherwise go silent for the length of a Revit round
 * trip, which reads as a hang. Naming the tool is also the only way a user can
 * tell "thinking" from "editing your model".
 *
 * **Flat rows, not a collapsible group.** The first pass here wrapped the calls
 * in a disclosure with a "3 steps" summary. That is not what the reference does
 * and it was the wrong shape twice over: a running turn's most informative line
 * was folded behind a control the user had to find, and once finished the group
 * left a widget in the transcript whose only content was provenance. The
 * reference lets each call be its own quiet line in the flow, present while it
 * matters and unremarkable afterwards — no chevron, no count, nothing to open.
 *
 * The rows are also **not indented under a header**, so a reply that ran three
 * tools reads as three short lines followed by an answer, rather than as a
 * nested structure the eye has to parse before reaching the point.
 */
import { useState } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import { humanizeToolName, toolLabelKey } from "../../lib/toolLabel";
import type { AgentStep } from "../../lib/chat";
import { generatesCode } from "../../lib/toolLabel";
import { Spinner } from "../settings/Spinner";
import { Markdown } from "./Markdown";
import { ArtifactDialog, type SaveSkillHandler } from "./ArtifactDialog";
import { SkillPatchCard } from "../skills/SkillPatchCard";

/** lucide `Wrench` — a settled call. */
function WrenchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </svg>
  );
}

/** lucide `XCircle` — a call that failed. */
function XCircleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="m15 9-6 6" />
      <path d="m9 9 6 6" />
    </svg>
  );
}

/**
 * lucide `RotateCcw` — a call that did not work. Not a red cross: a failed
 * call is usually one step in a turn the agent carries on with, and alarm
 * colour made every recoverable miss look like the whole turn had broken.
 */
function RetryIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
      <path d="M3 3v5h5" />
    </svg>
  );
}

/** lucide `CheckCircle2` — the small green tick beside a finished call. */
function CheckCircleIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

/** lucide `Code`. */
function CodeIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m16 18 6-6-6-6" />
      <path d="m8 6-6 6 6 6" />
    </svg>
  );
}

/**
 * "Show Source Code" opens the old web's read-only ToolEditor (`ArtifactDialog`,
 * item BX) — the code a tool actually ran, its details, and Save Skill.
 */
export type { SaveSkillHandler } from "./ArtifactDialog";

/** lucide `CircleSlash` — a call the user stopped. */
function CircleSlashIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <path d="m9 15 6-6" />
    </svg>
  );
}

/** lucide `Pencil` — a plan sent back for changes. */
function PencilIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
      <path d="m15 5 4 4" />
    </svg>
  );
}

/** lucide `MessageCircleQuestion` — a question the user answered. */
function QuestionIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <path d="M12 17h.01" />
    </svg>
  );
}

/**
 * What the user decided, where they decided it — the old web's decided
 * `HitlApprovalSection`: a small grey card (red for a rejection) with the
 * verdict in bold and the action's name under it. It stays in the flow so a
 * reply reads "asked → approved → ran", not "asked → ran".
 */
function DecisionRow({ step }: { step: AgentStep }) {
  const { t } = useLocale();
  const decision = step.decision;
  if (!decision) return null;
  const label = decision.title || t(decision.fallbackKey);
  return (
    <div className={`decision-row is-${decision.kind}`}>
      <span className="decision-row-icon" aria-hidden="true">
        {decision.kind === "approved" ? <CheckCircleIcon /> : decision.kind === "rejected" ? <XCircleIcon /> : decision.kind === "revised" ? <PencilIcon /> : <QuestionIcon />}
      </span>
      <span className="decision-row-body">
        <span className="decision-row-verdict">{t(`hitl.${decision.kind}`)}</span>
        <span className="decision-row-label">{label}</span>
        {decision.kind === "answered" && decision.answer && (
          <span className="decision-row-answer">{t("hitl.yourAnswer")}{decision.answer}</span>
        )}
      </span>
    </div>
  );
}

function ToolRow({ step, onShowCode }: { step: AgentStep; onShowCode: (() => void) | null }) {
  const { t } = useLocale();
  const key = toolLabelKey(step.tool);
  const name = step.label || (key ? t(key) : humanizeToolName(step.tool));
  const running = step.status === "running";
  const failed = step.status === "error";
  // Cancelled, not finished and not broken — so neither the green tick nor the
  // red cross. Saying nothing at all would leave it looking like a quiet success,
  // which is the thing pressing Stop most needs it not to look like.
  const stopped = step.status === "stopped";

  return (
    <div className={`tool-row is-${step.status}`}>
      <span className="tool-row-icon" aria-hidden="true">
        {running ? (
          <Spinner size={16} />
        ) : failed ? (
          <RetryIcon />
        ) : stopped ? (
          <CircleSlashIcon />
        ) : (
          <WrenchIcon />
        )}
      </span>
      <span className="tool-row-body">
        {running ? <span className="tool-row-calling">{t("common.calling")}</span> : null}
        {/* A model-written label is one line; an over-long one ends in an
            ellipsis with the full text on hover rather than wrapping the row. */}
        <span className={`tool-row-name${step.label ? " is-label" : ""}`} title={step.label}>{name}</span>
        {/* The reference's trailing ellipsis, which does the work a spinner
            cannot: it says the *name* is not the whole story yet. */}
        {running ? <span className="tool-row-ellipsis">…</span> : null}
        {step.status === "completed" ? (
          <span className="tool-row-tick" aria-hidden="true">
            <CheckCircleIcon />
          </span>
        ) : null}
        {failed ? <span className="tool-row-failed">{t("message.toolDidNotWork")}</span> : null}
        {stopped ? <span className="tool-row-stopped">{t("common.stopped")}</span> : null}
      </span>
      {onShowCode ? (
        <button type="button" className="tool-row-code" onClick={onShowCode}>
          <CodeIcon />
          {t("message.showSourceCode")}
        </button>
      ) : null}
    </div>
  );
}

export function AgentSteps({ steps, onSaveSkill = null }: { steps: AgentStep[]; onSaveSkill?: SaveSkillHandler | null }) {
  const [showing, setShowing] = useState<AgentStep | null>(null);

  if (steps.length === 0) return null;

  return (
    <div className="tool-rows">
      {steps.map((step, i) =>
        // Index is part of the key on purpose: the same tool called twice in one
        // turn is two rows, and keying on the name alone would collapse them.
        step.decision ? (
          <DecisionRow key={`${step.tool}-${i}`} step={step} />
        ) : (
          <div key={`${step.tool}-${i}`} className="tool-step">
            {/* What the model said before this call, where it said it. Drawn
                exactly as the answer drew it while streaming, so moving it here
                changes its place, not its look. */}
            {step.note ? <div className="tool-row-note"><Markdown text={step.note} /></div> : null}
            {step.candidatePatch ? (
              <SkillPatchCard step={step} />
            ) : (
              <ToolRow
                step={step}
                onShowCode={
                  generatesCode(step.tool) && typeof step.artifact?.["code"] === "string" ? () => setShowing(step) : null
                }
              />
            )}
            {step.images?.length ? (
              <div className="tool-step-images">
                {step.images.map((src, n) => (
                  <a key={src} className="tool-step-image" href={src} target="_blank" rel="noreferrer">
                    <img src={src} alt={`${step.tool} ${n + 1}`} />
                  </a>
                ))}
              </div>
            ) : null}
          </div>
        ),
      )}
      {showing?.artifact ? (
        <ArtifactDialog artifact={showing.artifact} onSaveSkill={onSaveSkill} onClose={() => setShowing(null)} />
      ) : null}
    </div>
  );
}
